import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import QuizContent from './QuizContent.js';
import SubjectTermsDialog from './study/SubjectTermsDialog.js';
import SettingsDialog from '../pages/settings/SettingsDialog.js';
import WrongAnswerNotebook from './study/WrongAnswerNotebook.js';
import Login from './auth/Login.js';
import SignUp from './auth/SignUp.js';
import LandingPage from './LandingPage.js';
import { useAuth } from '../context/AuthContext.js';
import { useApp } from '../context/AppContext.js';
import { ensureValidSession } from '../utils/auth.js';
import ProfileSettings from '../pages/settings/ProfileSettings.js';
import Pricing from '../pages/Pricing.js';
import SettingsPage from '../pages/SettingsPage.js';
import WrongAnswerDialog from './common/WrongAnswerDialog.js';
import Header from './Header.js';
import SuccessStoryDetailPage from '../pages/community/success-stories/[storyId].js';
import MyLearningPage from '../pages/my-learning/index.js';
import MyReviewPage from '../pages/my_review/index.js';

// 커뮤니티 페이지 컴포넌트들 임포트
import CommunityPage from '../pages/community/index.js';
import NoticesPage from '../pages/community/notices/index.js';
import SuccessStoriesPage from '../pages/community/success-stories/index.js';
import StudyGroupsPage from '../pages/community/study-groups/index.js';
import FreeBoardPage from '../pages/community/free-board/index.js';
import QnaPage from '../pages/community/casebrief/index.js';
import StudyTipsPage from '../pages/community/study-tips/index.js';
import FreeBoardDetail from '../pages/community/free-board/[postId].js';
import MptTestMainPage from '../pages/mpt_test/index.js';
import CaseBriefDetail from '../pages/community/casebrief/[id].js';
import MbeTestMainPage from '../pages/mbe_test/index.js';
import TestPage from '../pages/mbe_test/TestPage.js';
import MbeAdminMainPage from '../pages/mbe_admin/index.js';
import MeeTestMainPage from '../pages/mee_test/index.js';
import EvaluatorDashboard from '../pages/evaluator/index.js';
import EvaluationDetailPage from '../pages/evaluator/EvaluationDetailPage.js';

const MainContent = () => {
  const { 
    isLoggedIn, 
    isAdmin, 
    user, 
    signup,
    logout,
    isEvaluator,
    loading: isAuthLoading,
    userProfile,
    signupSuccessDialog,
    setSignupSuccessDialog
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    openSettings,
    setOpenSettings,
    openWrongAnswers,
    setOpenWrongAnswers,
    isLoading: isAppLoading
  } = useApp();

  // 전역 세션 상태 관리
  useEffect(() => {
    if (isLoggedIn && user) {
      // 페이지 로드 시 세션 유효성 검사
      const checkInitialSession = async () => {
        const session = await ensureValidSession();
        if (!session) {
          console.log('초기 세션 검증 실패. 로그아웃 처리합니다.');
          logout();
        }
      };

      checkInitialSession();

      // 주기적 세션 검증 (10분마다)
      const sessionCheckInterval = setInterval(async () => {
        const session = await ensureValidSession();
        if (!session) {
          console.log('주기적 세션 검증 실패. 로그아웃 처리합니다.');
          logout();
        }
      }, 10 * 60 * 1000); // 10분마다 확인

      return () => clearInterval(sessionCheckInterval);
    }
  }, [isLoggedIn, user, logout]);

  // 페이지 가시성 변경 시 세션 검증 (탭 전환 등)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isLoggedIn && user) {
        const session = await ensureValidSession();
        if (!session) {
          console.log('페이지 재활성화 시 세션 검증 실패. 로그아웃 처리합니다.');
          logout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isLoggedIn, user, logout]);

  const handleWrongAnswersClose = () => {
    setOpenWrongAnswers(false);
  };

  const handleSettingsClose = () => {
    setOpenSettings(false);
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleLoginClose = () => {
    navigate(-1);
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleSignupClose = () => {
    navigate(-1);
  };

  const handleWrongAnswersClick = () => {
    navigate('/my-learning');
  };

  // isEvaluator 값 확인을 위한 로그 추가
  useEffect(() => {
    console.log('Auth state in MainContent:', { isLoggedIn, isEvaluator, user });
  }, [isLoggedIn, isEvaluator, user]);

  // Show a global loader while authentication or essential app data is loading
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {location.pathname !== '/login' && location.pathname !== '/signup' && (
          <Header
            isLoggedIn={isLoggedIn}
            isAdmin={isAdmin}
            user={user}
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
            onWrongAnswersClick={handleWrongAnswersClick}
            isLandingPage={!isLoggedIn && location.pathname === '/'}
          />
        )}
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Routes>
            <Route 
              path="/" 
              element={
                !isLoggedIn ? (
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <LandingPage onLoginClick={handleLoginClick} onSignupClick={handleSignupClick} />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', height: '100%', pt: 2 }}>
                    <QuizContent />
                  </Box>
                )
              } 
            />
            <Route path="/login" element={<Login open={location.pathname === '/login'} onClose={handleLoginClose} />} />
            <Route path="/signup" element={
              <SignUp 
                open={location.pathname === '/signup'} 
                onClose={handleSignupClose} 
                onSignUp={signup}
              />
            } />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/settings" element={
              isLoggedIn ? <SettingsPage /> : <Navigate to="/" replace />
            } />
            <Route path="/settings/profile" element={
              isLoggedIn ? <ProfileSettings /> : <Navigate to="/" replace />
            } />
            <Route path="/my-learning" element={
              isLoggedIn ? <MyLearningPage /> : <Navigate to="/" replace />
            } />
            <Route path="/wrong-answers" element={
              isLoggedIn ? <Navigate to="/my-learning" replace /> : <Navigate to="/" replace />
            } />

            {/* 커뮤니티 관련 라우트 */}
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/community/notices" element={<NoticesPage />} />
            <Route path="/community/success-stories" element={<SuccessStoriesPage />} />
            <Route path="/community/success-stories/:storyId" element={<SuccessStoryDetailPage />} />
            <Route path="/community/study-groups/*" element={<StudyGroupsPage />} />
            <Route path="/community/free-board" element={<FreeBoardPage />} />
            <Route path="/community/casebrief" element={<QnaPage />} />
            <Route path="/community/study-tips" element={<StudyTipsPage />} />
            <Route path="/community/free-board/:postId" element={<FreeBoardDetail />} />
            <Route path="/community/casebrief/:id" element={<CaseBriefDetail />} />

            {/* MPT 실전 테스트 라우트 */}
            <Route path="/mpt_test" element={<MptTestMainPage />} />

            {/* MEE 실전 테스트 라우트 */}
            <Route path="/mee_test" element={<MeeTestMainPage />} />

            {/* MBE 실전 테스트 라우트 */}
            <Route path="/mbe_test" element={<MbeTestMainPage />} />
            <Route path="/mbe_test/test" element={<TestPage />} />

            {/* MBE 관리 테스트 라우트 */}
            <Route path="/mbe_admin" element={<MbeAdminMainPage />} />

            {/* 복습/비교 라우트 */}
            <Route path="/my_review" element={<MyReviewPage />} />

            {/* 평가자 전용 라우트 */}
            <Route path="/evaluator" element={
              isEvaluator ? <EvaluatorDashboard /> : <Navigate to="/" replace />
            } />
            <Route path="/evaluator/evaluate/:answerType/:answerId" element={
              isEvaluator ? <EvaluationDetailPage /> : <Navigate to="/" replace />
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Box>
      <WrongAnswerDialog open={openWrongAnswers} onClose={handleWrongAnswersClose} />
      <SettingsDialog open={openSettings} onClose={handleSettingsClose} />
      
      {/* 회원가입 완료 안내 다이얼로그 */}
      <Dialog 
        open={signupSuccessDialog} 
        onClose={() => setSignupSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          fontSize: '1.5rem',
          py: 2
        }}>
          회원가입 신청 완료
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', mb: 1 }}>
            회원가입 절차가 다음과 같이 진행됩니다:
          </Typography>
          <Box component="ol" sx={{ ml: 2, mb: 2 }}>
            <Typography component="li" sx={{ mb: 0.5 }}>
              입력하신 이메일로 인증 메일이 발송되었습니다.
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              이메일에 포함된 링크를 클릭하여 이메일 인증을 완료해 주세요.
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              관리자 승인 후 로그인이 가능합니다.
            </Typography>
          </Box>
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              bgcolor: 'warning.light', 
              color: 'warning.dark',
              p: 1.5,
              borderRadius: 1,
              fontWeight: 'medium',
              mb: 1
            }}
          >
            ⚠️ 이메일이 스팸메일함에 있을 수 있으니 확인해 주세요!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'center' }}>
          <Button 
            onClick={() => setSignupSuccessDialog(false)} 
            variant="contained" 
            size="large"
            sx={{ minWidth: 100 }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MainContent;
